import React from 'react';

import { Stack, CheckCircleIcon, ErrorOutlineIcon } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import { ScreenLink, useScreenDispatch, useScreenState } from '@gogame/screens';

import { BusinessPartnerFormEdit } from 'scenes/BusinessPartners/components/BusinessPartnerForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_DPA_STATUS,
  COLUMN_CUSTOMER_COMPANY,
  COLUMN_PARTNER_COMPANY,
  COLUMN_NETWORK,
  COLUMN_INVOICE_LEVEL,
  COLUMN_CURRENCY,
  COLUMN_EXPENSE_TYPES,
  COLUMN_OWNER,
  COLUMN_CREATED_BY,
  COLUMN_UPDATED_DATE,
  COLUMN_CREATED_DATE,
} from 'scenes/BusinessPartners/constants';
import {
  useBusinessPartnerGrid,
  useIsUpdating,
} from 'scenes/BusinessPartners/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/BusinessPartners/screenReducer';
import PartnerCompanyDetails from 'scenes/PartnerCompanies/components/PartnerCompanyDetails';
import StatusComponent from 'scenes/BusinessPartners/components/Status';
import BPActions from 'scenes/BusinessPartners/components/Actions';
import { businessPartnersDetailsLink } from 'scenes/BusinessPartners/navigation';

import Toolbar from './Toolbar';

const BusinessPartnersDetailsLink = ({ children, row }) => (
  <ScreenLink
    link={businessPartnersDetailsLink}
    pathParams={{
      id: row.id,
    }}
  >
    {children}
  </ScreenLink>
);

const BusinessPartnerDataTable = () => {
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const [childComponent, openChildComponent] = useChildComponent();

  const { data, isLoading, isRefetching } = useBusinessPartnerGrid({
    filters: screenState.filters,
    searchQuery: screenState.searchQuery,
    tableSettings: screenState.tableSettings,
  });

  const isUpdating = useIsUpdating();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleOpenPartnerCompany = (_e, { id }) => {
    openChildComponent(PartnerCompanyDetails, { id });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(BusinessPartnerFormEdit, { id });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        loading={isLoading || isUpdating}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        storageKey={'business-partners'}
        defaultColumnWidth={90}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={90}
          fixed={true}
          resizable={false}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'link'}
          headerName={'Name'}
          width={280}
          linkComponent={BusinessPartnersDetailsLink}
          getTooltip={(v) => v}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          width={110}
          formatter={(value, row) => (
            <StatusComponent id={value} isSapSynced={row.isSapSynced} />
          )}
        />
        <DataGrid.Column
          field={COLUMN_DPA_STATUS}
          headerName={'DPA Status'}
          width={130}
          formatter={(value) =>
            value ? (
              <Stack alignItems={'center'} spacing={1} direction={'row'}>
                <CheckCircleIcon color={'success'} fontSize={'small'} />
                <span>Signed</span>
              </Stack>
            ) : (
              <Stack alignItems={'center'} spacing={1} direction={'row'}>
                <ErrorOutlineIcon color={'error'} fontSize={'small'} />
                <span>No valid DPA</span>
              </Stack>
            )
          }
        />
        <DataGrid.Column
          field={COLUMN_PARTNER_COMPANY}
          type={'link'}
          headerName={'Partner Company'}
          onLinkClick={handleOpenPartnerCompany}
          width={170}
          getTooltip={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_CUSTOMER_COMPANY}
          type={'text'}
          headerName={'Customer Company'}
          width={206}
        />
        <DataGrid.Column
          field={COLUMN_NETWORK}
          type={'text'}
          headerName={'Network'}
          width={206}
        />
        <DataGrid.Column
          field={COLUMN_INVOICE_LEVEL}
          type={'text'}
          headerName={'Invoice Level'}
          width={115}
        />
        <DataGrid.Column
          field={COLUMN_CURRENCY}
          type={'text'}
          headerName={'Currency'}
          formatter={(v) => v?.toUpperCase()}
          width={125}
        />
        <DataGrid.Column
          field={COLUMN_EXPENSE_TYPES}
          type={'text'}
          headerName={'Expense Types'}
          width={120}
        />
        <DataGrid.Column
          field={COLUMN_OWNER}
          type={'text'}
          headerName={'Owner'}
          width={160}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED_DATE}
          headerName={'Last Updated'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          type={'text'}
          headerName={'Created by'}
          width={160}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={160}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          actionsColDef
          field={'actions'}
          type={'actions'}
          actionsComponent={BPActions}
          onEdit={handleEdit}
          width={157}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default BusinessPartnerDataTable;
